import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from "sonner";
import { ArrowLeft, Upload, Loader2 } from 'lucide-react';

export function UserFormScreen() {
    const { id } = useParams();
    const isEdit = !!id;
    const navigate = useNavigate();

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [photo, setPhoto] = useState(''); // URL or base64 placeholder

    const { data: user, isLoading: isLoadingUser } = trpc.getUserDetails.useQuery(
        { id: id! },
        { enabled: isEdit }
    );

    useEffect(() => {
        if (user && user.user) {
            setName(user.user.name);
            setEmail(user.user.email);
            setPhone(user.user.phone);
            if (user.user.metadata) {
                const meta = JSON.parse(user.user.metadata);
                setAddress(meta.address || '');
                setPhoto(meta.photo || '');
            }
        }
    }, [user]);

    const createMutation = trpc.createUser.useMutation({
        onSuccess: () => {
            toast.success("User created");
            navigate('/users');
        },
        onError: (err) => toast.error(err.message)
    });

    const updateMutation = trpc.updateUser.useMutation({
        onSuccess: () => {
            toast.success("User updated");
            navigate(`/users/${id}`);
        },
        onError: (err) => toast.error(err.message)
    });

    const handleSubmit = () => {
        if (!name || !email || !phone) {
            toast.error("Name, Email and Phone are required");
            return;
        }

        if (isEdit) {
            updateMutation.mutate({
                id: id!,
                name, email, phone, address, photo
            });
        } else {
            createMutation.mutate({
                name, email, phone, address, photo
            });
        }
    };

    if (isEdit && isLoadingUser) {
        return <div className="p-8 flex justify-center"><Loader2 className="animate-spin" /></div>;
    }

    return (
        <div className="min-h-screen bg-background p-4 space-y-6 pb-20">
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <h1 className="text-xl font-bold">{isEdit ? 'Edit User' : 'Add New User'}</h1>
            </div>

            <div className="space-y-4">
                {/* Photo Upload Placeholder */}
                <div className="flex justify-center mb-6">
                    <div className="relative">
                        <div className="h-24 w-24 rounded-full bg-muted flex items-center justify-center overflow-hidden border-2 border-dashed border-muted-foreground/30">
                            {photo ? (
                                <img src={photo} alt="Preview" className="h-full w-full object-cover" />
                            ) : (
                                <span className="text-xs text-muted-foreground text-center px-2">No Photo</span>
                            )}
                        </div>
                        <Button
                            variant="secondary"
                            size="icon"
                            className="absolute bottom-0 right-0 h-8 w-8 rounded-full shadow"
                            onClick={() => {
                                // Simplified: Prompt for URL
                                const url = prompt("Enter image URL");
                                if (url) setPhoto(url);
                            }}
                        >
                            <Upload className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                <div className="space-y-2">
                    <label className="text-sm font-medium">Full Name</label>
                    <Input value={name} onChange={e => setName(e.target.value)} placeholder="John Doe" />
                </div>

                <div className="space-y-2">
                    <label className="text-sm font-medium">Email</label>
                    <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="john@example.com" />
                </div>

                <div className="space-y-2">
                    <label className="text-sm font-medium">Phone</label>
                    <Input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+91 0000000000" />
                </div>

                <div className="space-y-2">
                    <label className="text-sm font-medium">Address</label>
                    <Input value={address} onChange={e => setAddress(e.target.value)} placeholder="Street, City, Zip" />
                </div>

                <Button className="w-full mt-6" size="lg" onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending}>
                    {isEdit ? 'Save Changes' : 'Create User'}
                </Button>
            </div>
        </div>
    );
}
